// src/pages/ModeratorCriteriasPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../api/axios";
import "./ModeratorCriteriasPage.css";

const PAGE_SIZE = 10;

/* ===========================
   Searchable Select (no libs)
   - поиск по списку
   - закрытие по клику вне
   - закрытие по Esc
   - не ломает твои mc-* стили
=========================== */
function SearchSelect({
  value,
  onChange,
  options,
  placeholder,
  searchPlaceholder,
  locale = "kz",
  disabled = false,
  clearable = false,
  clearLabel,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => String(o.value) === String(value)),
    [options, value]
  );

  const filtered = useMemo(() => {
    const s = String(q || "").toLowerCase().trim();
    if (!s) return options;
    return options.filter((o) => String(o.label).toLowerCase().includes(s));
  }, [options, q]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setOpen(false);
    setQ("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const pick = (val) => {
    onChange(val === "" ? "" : String(val));
    setOpen(false);
    setQ("");
  };

  return (
    <div className={`mc-ss ${disabled ? "mc-ss--disabled" : ""}`} ref={rootRef}>
      <button
        type="button"
        className="mc-ss-btn"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={selected ? "mc-ss-val" : "mc-ss-ph"}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="mc-ss-caret">▾</span>
      </button>

      {open && !disabled && (
        <div
          className="mc-ss-pop"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            className="mc-ss-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            autoFocus
          />

          <div className="mc-ss-list">
            {clearable && (
              <button
                key="__clear__"
                type="button"
                className={value === "" ? "mc-ss-item mc-ss-item--active" : "mc-ss-item"}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  pick("");
                }}
              >
                {clearLabel}
              </button>
            )}

            {filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                className={
                  String(o.value) === String(value)
                    ? "mc-ss-item mc-ss-item--active"
                    : "mc-ss-item"
                }
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  pick(o.value);
                }}
                title={o.label}
              >
                {o.label}
              </button>
            ))}

            {filtered.length === 0 && (
              <div className="mc-ss-empty">
                {locale === "kz" ? "Ештеңе табылмады" : "Ничего не найдено"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ===========================================================
   MultiSelect (subjects) — no external libs
   - поиск по названию
   - чекбоксы, чипы выбранных
   - явная кнопка "очистить" -> делает критерий универсальным
   - закрытие по клику вне / Esc
=========================================================== */
function SubjectsMultiSelect({
  value = [],
  onChange,
  options,
  locale = "kz",
  placeholder,
  searchPlaceholder,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const rootRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // раскрываем список прямо в потоке формы, поэтому при открытии
  // подскролливаем его в видимую зону модалки, а не даём ему уходить за край
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      popRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const selectedSet = useMemo(() => new Set(value.map((v) => Number(v))), [value]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    if (!s) return options;
    return options.filter((o) => o.label.toLowerCase().includes(s));
  }, [options, q]);

  const toggle = (id) => {
    const idNum = Number(id);
    if (selectedSet.has(idNum)) {
      onChange(value.filter((v) => Number(v) !== idNum));
    } else {
      onChange([...value, idNum]);
    }
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const selectedOptions = useMemo(
    () => options.filter((o) => selectedSet.has(Number(o.value))),
    [options, selectedSet]
  );

  const isUniversal = value.length === 0;

  return (
    <div className={`mc-ms ${disabled ? "mc-ms--disabled" : ""}`} data-open={open ? "true" : "false"} ref={rootRef}>
      <button type="button" className="mc-ms-btn" disabled={disabled} onClick={() => setOpen((v) => !v)}>
        {isUniversal ? (
          <span className="mc-ms-universal-tag">
            <span className="mc-ms-globe">🌐</span>
            {locale === "kz" ? "Барлық пәндерге (универсал)" : "Все предметы (универсальный)"}
          </span>
        ) : (
          <div className="mc-ms-chips">
            {selectedOptions.map((o) => (
              <span key={o.value} className="mc-chip">
                {o.label}
                <span
                  className="mc-chip-x"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(o.value);
                  }}
                >
                  ✕
                </span>
              </span>
            ))}
          </div>
        )}
        <span className="mc-ms-caret">▾</span>
      </button>

      {!isUniversal && !disabled && (
        <button type="button" className="mc-ms-clear" onClick={clearAll}>
          {locale === "kz" ? "Тазарту (универсал ету)" : "Очистить (сделать универсальным)"}
        </button>
      )}

      {open && !disabled && (
        <div
          className="mc-ms-pop"
          ref={popRef}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            className="mc-ms-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            autoFocus
          />
          <div className="mc-ms-list">
            {filtered.map((o) => {
              const checked = selectedSet.has(Number(o.value));
              return (
                <label key={o.value} className="mc-ms-item">
                  <input type="checkbox" checked={checked} onChange={() => toggle(o.value)} />
                  <span>{o.label}</span>
                </label>
              );
            })}
            {filtered.length === 0 && (
              <div className="mc-ms-empty">
                {locale === "kz" ? "Ештеңе табылмады" : "Ничего не найдено"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ModeratorCriteriasPage({ locale = "kz" }) {
  const [criteriaCategories, setCriteriaCategories] = useState([]); // /backoffice/criteria-categories
  const [forms, setForms] = useState([]); // /backoffice/ref/criteria-forms
  const [subjects, setSubjects] = useState([]); // /backoffice/ref/subjects
  const [criterias, setCriterias] = useState([]); // /backoffice/criterias

  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  // индикаторы всегда рендерим как 4 слота (баллы 0..3), даже если данных нет —
  // так проще собирать форму и не терять место под ещё не заполненный балл
  const makeEmptyIndicators = () =>
    [0, 1, 2, 3].map((score) => ({ score, description_kz: "", description_ru: "" }));

  const emptyForm = {
    name_kz: "",
    name_ru: "",
    max_point: "",
    criteria_category_id: "",
    criteria_form_id: "",
    subject_ids: [], // [] = универсальный
    descriptor_kz: "",
    descriptor_ru: "",
    indicators: makeEmptyIndicators(),
  };
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const t = useMemo(
    () => ({
      kz: {
        title: "Критерийлер тізімі",
        add: "Қосу",
        edit: "Өңдеу",
        save: "Сақтау",
        del: "Жою",
        empty: "Жазбалар жоқ",
        search: "Іздеу (ID, атауы, категория, форма, пән...)",
        req: "Барлық міндетті өрістерді толтырыңыз",
        err: "Қате орын алды",
        confirmDel: "Осы критерийді жоюға сенімдісіз бе?",
        colName: "Атауы",
        colMax: "Макс. ұпай",
        colCritCat: "Категория",
        colForm: "Форма",
        colSubject: "Пән",
        colAction: "Әрекет",
        universal: "Универсал",
        modalAdd: "Критерий қосу",
        modalEdit: "Критерийді өңдеу",
        nameKz: "Атауы (қазақша)",
        nameRu: "Атауы (орысша)",
        maxPoint: "Максималды ұпай",
        pickCritCat: "Категорияны таңдаңыз",
        pickForm: "Форманы таңдаңыз",
        pickSubject: "Пәнді таңдаңыз",
        subjectHint: "Таңдалмаса — критерий кез келген пәнге қолданылады (универсал).",
        searchPh: "Іздеу...",
        page: "Бет",
        per: "бет",
        loading: "Жүктелуде...",
        forbidden: "Сізде бұл бетке қол жеткізу құқығы жоқ (модератор рөлі қажет).",
        cancel: "Болдырмау",
        requiredField: "Міндетті өріс",
        maxPointRange: "1-ден 100-ге дейінгі сан болуы керек",
        descriptor: "Дескриптор",
        descriptorKz: "Индикатор (қазақша)",
        descriptorRu: "Индикатор (орысша)",
        indecatorKZ: "Дескриптор (қазақша)",
        indecatorRU: "Дескриптор (орысша)",
        descriptorHint: "Критерийге жалпы түсіндірме мәтін, міндетті емес.",
        indicators: "Дескрипторлар",
        indicatorsHint:
          "Әр балл үшін қысқаша түсіндірме (міндетті емес, толықтырылмаған баллдарды бос қалдыруға болады).",
        indicatorScore: (s) => `Балл ${s}`,
        hasDescriptorTitle: "Дескриптор толтырылған",
        hasIndicatorsTitle: "Индикаторлар толтырылған",
        noSantat: "санатсыз",
      },
      ru: {
        title: "Список критериев",
        add: "Добавить",
        edit: "Редактировать",
        save: "Сохранить",
        del: "Удалить",
        empty: "Записей нет",
        search: "Поиск (ID, название, категория, форма, предмет...)",
        req: "Заполните все обязательные поля",
        err: "Произошла ошибка",
        confirmDel: "Вы уверены, что хотите удалить критерий?",
        colName: "Название",
        colMax: "Макс. балл",
        colCritCat: "Категория",
        colForm: "Форма",
        colSubject: "Предмет",
        colAction: "Действие",
        universal: "Универсальный",
        modalAdd: "Добавить критерий",
        modalEdit: "Редактирование критерия",
        nameKz: "Название (каз)",
        nameRu: "Название (рус)",
        maxPoint: "Максимальный балл",
        pickCritCat: "Выберите категорию",
        pickForm: "Выберите форму",
        pickSubject: "Выберите предмет",
        subjectHint: "Не выбрано — критерий подходит любому предмету (универсальный).",
        searchPh: "Поиск...",
        page: "Стр.",
        per: "стр.",
        loading: "Загрузка...",
        forbidden: "У вас нет доступа к этой странице (требуется роль модератора).",
        cancel: "Отмена",
        requiredField: "Обязательное поле",
        maxPointRange: "Должно быть числом от 1 до 100",
        descriptor: "Дескриптор",
        descriptorKz: "Дескриптор (каз)",
        descriptorRu: "Дескриптор (рус)",
        descriptorHint: "Общий поясняющий текст к критерию, необязательно.",
        indicators: "Индикаторы",
        indicatorsHint:
          "Короткая подсказка для каждого балла (необязательно, незаполненные баллы можно оставить пустыми).",
        indicatorScore: (s) => `Балл ${s}`,
        hasDescriptorTitle: "Дескриптор заполнен",
        hasIndicatorsTitle: "Индикаторы заполнены",
        noSantat: "без саната",
      },
    }),
    []
  );

  const L = locale === "kz" ? t.kz : t.ru;

  const fetchAll = async () => {
    setLoading(true);
    setAccessDenied(false);
    setLoadError("");
    try {
      const [critRes, critCatRes, formRes, subjRes] = await Promise.all([
        axios.get("/backoffice/criterias"),
        axios.get("/backoffice/criteria-categories"),
        axios.get("/backoffice/ref/criteria-forms"),
        axios.get("/backoffice/ref/subjects"),
      ]);

      const sorted = [...(critRes.data || [])].sort((a, b) => (b?.id ?? 0) - (a?.id ?? 0));

      setCriterias(sorted);
      setCriteriaCategories(Array.isArray(critCatRes.data) ? critCatRes.data : []);
      setForms(Array.isArray(formRes.data) ? formRes.data : []);
      setSubjects(Array.isArray(subjRes.data) ? subjRes.data : []);
      setPage(1);
    } catch (err) {
      if (err?.response?.status === 403) {
        setAccessDenied(true);
      } else {
        setLoadError(err?.response?.data?.message || L.err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAddModal = () => {
    setFormData(emptyForm);
    setFormErrors({});
    setEditingId(null);
    setIsEditing(false);
    setModalOpen(true);
  };

  const openEditModal = (criteria) => {
    const byScore = new Map();
    (Array.isArray(criteria?.indicators) ? criteria.indicators : []).forEach((ind) => {
      byScore.set(Number(ind.score), ind);
    });
    const indicators = [0, 1, 2, 3].map((score) => {
      const ind = byScore.get(score);
      return {
        score,
        description_kz: ind?.description_kz || "",
        description_ru: ind?.description_ru || "",
      };
    });

    setFormData({
      name_kz: criteria?.name_kz || "",
      name_ru: criteria?.name_ru || "",
      max_point: criteria?.max_point ?? "",
      criteria_category_id: criteria?.criteria_category_id ?? "",
      criteria_form_id: criteria?.criteria_form_id ?? "",
      subject_ids: Array.isArray(criteria?.subject_ids) ? criteria.subject_ids.map(Number) : [],
      descriptor_kz: criteria?.descriptor_kz || "",
      descriptor_ru: criteria?.descriptor_ru || "",
      indicators,
    });
    setFormErrors({});
    setEditingId(criteria?.id);
    setIsEditing(true);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const criteriaCategoryOptions = useMemo(
    () =>
      criteriaCategories.map((c) => {
        const baseName = locale === "kz" ? c.name_kz : c.name_ru;
        const santatNames = (Array.isArray(c.categories) ? c.categories : []).map(
          (s) => (locale === "kz" ? s.name_kz : s.name_ru) || s.name || ""
        ).filter(Boolean);
        const suffix = santatNames.length > 0 ? ` — ${santatNames.join(", ")}` : ` — ${L.noSantat}`;
        return {
          value: c.id,
          label: `${baseName}${suffix}`,
        };
      }),
    [criteriaCategories, locale, L.noSantat]
  );

  const formOptions = useMemo(() => forms.map((f) => ({ value: f.id, label: f.name })), [forms]);

  const subjectOptions = useMemo(
    () =>
      subjects.map((s) => ({
        value: s.id,
        label: locale === "kz" ? s.name_kz || s.name : s.name_ru || s.name,
      })),
    [subjects, locale]
  );

  const filteredCriterias = useMemo(() => {
    const s = String(q || "").toLowerCase().trim();
    if (!s) return criterias;

    return criterias.filter((c) => {
      const id = String(c?.id ?? "");
      const nkz = String(c?.name_kz || "").toLowerCase();
      const nru = String(c?.name_ru || "").toLowerCase();
      const critCatKz = String(c?.criteria_category_kz || "").toLowerCase();
      const critCatRu = String(c?.criteria_category_ru || "").toLowerCase();
      const formKz = String(c?.form_kz || "").toLowerCase();
      const formRu = String(c?.form_ru || "").toLowerCase();
      const subjNames = (c?.subjects || [])
        .map((sub) => `${sub?.name_kz || ""} ${sub?.name_ru || ""}`.toLowerCase())
        .join(" ");

      return (
        id.includes(s) ||
        nkz.includes(s) ||
        nru.includes(s) ||
        critCatKz.includes(s) ||
        critCatRu.includes(s) ||
        formKz.includes(s) ||
        formRu.includes(s) ||
        subjNames.includes(s)
      );
    });
  }, [q, criterias]);

  const totalPages = Math.max(1, Math.ceil(filteredCriterias.length / PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const currentItems = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredCriterias.slice(start, start + PAGE_SIZE);
  }, [filteredCriterias, page]);

  const goTo = (p) => setPage(Math.min(Math.max(1, p), totalPages));

  const validate = () => {
    const errs = {};
    if (!formData.name_kz?.trim()) errs.name_kz = L.requiredField;
    if (!formData.name_ru?.trim()) errs.name_ru = L.requiredField;

    const maxStr = String(formData.max_point).trim();
    if (maxStr === "") {
      errs.max_point = L.requiredField;
    } else {
      const p = Number(maxStr);
      if (Number.isNaN(p) || p < 1 || p > 100) {
        errs.max_point = L.maxPointRange;
      }
    }

    if (String(formData.criteria_category_id).trim() === "") {
      errs.criteria_category_id = L.requiredField;
    }
    if (String(formData.criteria_form_id).trim() === "") {
      errs.criteria_form_id = L.requiredField;
    }

    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    setFormErrors(errs);
    if (Object.keys(errs).length > 0) return;

    // индикаторы: отправляем только те баллы, где заполнено хотя бы одно из полей —
    // пустые слоты (не тронутые модератором) в payload не попадают
    const indicatorsPayload = (formData.indicators || [])
      .filter((ind) => ind.description_kz?.trim() || ind.description_ru?.trim())
      .map((ind) => ({
        score: Number(ind.score),
        description_kz: ind.description_kz?.trim() || "",
        description_ru: ind.description_ru?.trim() || "",
      }));

    const payload = {
      name_kz: formData.name_kz.trim(),
      name_ru: formData.name_ru.trim(),
      max_point: Number(formData.max_point),
      criteria_category_id: Number(formData.criteria_category_id),
      criteria_form_id: Number(formData.criteria_form_id),
      // subject_ids: [] = универсальный (снимает все привязки); иначе — полная замена списка предметов
      subject_ids: (formData.subject_ids || []).map(Number),
      descriptor_kz: formData.descriptor_kz.trim(),
      descriptor_ru: formData.descriptor_ru.trim(),
      // indicators: полная замена списка (как и subject_ids/categories выше) — что не прислали, то удалится
      indicators: indicatorsPayload,
    };

    setSaving(true);
    try {
      if (isEditing) {
        await axios.put(`/backoffice/criterias/${editingId}`, payload);
      } else {
        await axios.post("/backoffice/criterias", payload);
      }
      await fetchAll();
      setModalOpen(false);
      setPage(1);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) {
        setAccessDenied(true);
        setModalOpen(false);
      } else if (status === 422) {
        const data = err?.response?.data;
        const serverErrors = data?.errors || {};
        const mapped = {};
        Object.keys(serverErrors).forEach((key) => {
          const msgs = serverErrors[key];
          mapped[key] = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
        });
        setFormErrors((prev) => ({ ...prev, ...mapped }));
        if (Object.keys(mapped).length === 0 && data?.message) {
          setFormErrors((prev) => ({ ...prev, _general: data.message }));
        }
      } else {
        setFormErrors((prev) => ({ ...prev, _general: err?.response?.data?.message || L.err }));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditing || !editingId) return;
    if (!window.confirm(L.confirmDel)) return;

    setSaving(true);
    try {
      await axios.delete(`/backoffice/criterias/${editingId}`);
      await fetchAll();
      setModalOpen(false);
      setPage(1);
    } catch (err) {
      if (err?.response?.status === 403) {
        setAccessDenied(true);
        setModalOpen(false);
      } else {
        alert(err?.response?.data?.message || L.err);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mc-page">
        <div className="mc-status">{L.loading}</div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="mc-page">
        <div className="mc-forbidden">🚫 {L.forbidden}</div>
      </div>
    );
  }

  return (
    <div className="mc-page">
      <div className="mc-head">
        <h2>🧮 {L.title}</h2>
        <button className="mc-btn mc-btn-primary" onClick={openAddModal} type="button">
          ➕ {L.add}
        </button>
      </div>

      {loadError && <div className="mc-error-banner">{loadError}</div>}

      <div className="mc-toolbar">
        <input
          className="mc-search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          placeholder={L.search}
        />
        {!!q && (
          <button
            className="mc-clear"
            type="button"
            onClick={() => {
              setQ("");
              setPage(1);
            }}
          >
            ✕
          </button>
        )}
      </div>

      <div className="mc-table-wrap">
        <table className="mc-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>{L.colName}</th>
              <th>{L.colMax}</th>
              <th>{L.colCritCat}</th>
              <th>{L.colForm}</th>
              <th>{L.colSubject}</th>
              <th>{L.colAction}</th>
            </tr>
          </thead>

          <tbody>
            {currentItems.map((c) => {
              const isUniversal =
                c.is_subject_universal === true || !Array.isArray(c.subjects) || c.subjects.length === 0;
              const hasDescriptor = Boolean(c.descriptor_kz?.trim() || c.descriptor_ru?.trim());
              const hasIndicators = Array.isArray(c.indicators)
                ? c.indicators.some((ind) => ind?.description_kz?.trim() || ind?.description_ru?.trim())
                : false;
              return (
                <tr key={c.id}>
                  <td>{c.id}</td>
                  <td>
                    {locale === "kz" ? c.name_kz : c.name_ru}
                    {(hasDescriptor || hasIndicators) && (
                      <span className="mc-help-flags">
                        {hasDescriptor && (
                          <span className="mc-help-flag mc-help-flag--descriptor" title={L.hasDescriptorTitle}>
                            !
                          </span>
                        )}
                        {hasIndicators && (
                          <span className="mc-help-flag mc-help-flag--indicators" title={L.hasIndicatorsTitle}>
                            i
                          </span>
                        )}
                      </span>
                    )}
                  </td>
                  <td>{c.max_point}</td>
                  <td>{locale === "kz" ? c.criteria_category_kz : c.criteria_category_ru}</td>
                  <td>{locale === "kz" ? c.form_kz : c.form_ru}</td>
                  <td>
                    {isUniversal ? (
                      <span className="mc-badge mc-badge--universal">🌐 {L.universal}</span>
                    ) : (
                      <div className="mc-subject-tags">
                        {c.subjects.map((sub) => (
                          <span key={sub.id} className="mc-badge">
                            {locale === "kz" ? sub.name_kz : sub.name_ru}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td>
                    <button
                      className="mc-btn mc-btn-ghost mc-btn-sm"
                      type="button"
                      onClick={() => openEditModal(c)}
                    >
                      ✏️ {L.edit}
                    </button>
                  </td>
                </tr>
              );
            })}

            {currentItems.length === 0 && (
              <tr>
                <td colSpan={7} className="mc-empty">
                  {L.empty}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mc-pager">
        <button className="mc-pager-btn" onClick={() => goTo(1)} disabled={page === 1}>
          ⏮
        </button>
        <button className="mc-pager-btn" onClick={() => goTo(page - 1)} disabled={page === 1}>
          ‹
        </button>
        <span className="mc-pager-info">
          {L.page} {page}/{totalPages}
          <span className="mc-pager-sep"> • </span>
          {PAGE_SIZE} / {L.per}
        </span>
        <button className="mc-pager-btn" onClick={() => goTo(page + 1)} disabled={page === totalPages}>
          ›
        </button>
        <button className="mc-pager-btn" onClick={() => goTo(totalPages)} disabled={page === totalPages}>
          ⏭
        </button>
      </div>

      {modalOpen && (
        <div
          className="mc-backdrop"
          onClick={(e) => {
            if (e.target.classList.contains("mc-backdrop")) closeModal();
          }}
        >
          <div
            className="mc-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="mc-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="mc-modal-title" className="mc-title">
              {isEditing ? L.modalEdit : L.modalAdd}
            </h3>

            {formErrors._general && <div className="mc-error-banner">{formErrors._general}</div>}

            <div className="mc-form-grid">
              <label className="mc-field">
                <span className="mc-label">{L.nameKz}</span>
                <input
                  type="text"
                  value={formData.name_kz}
                  onChange={(e) => setFormData({ ...formData, name_kz: e.target.value })}
                  className={formErrors.name_kz ? "mc-input-error" : ""}
                />
                {formErrors.name_kz && <small className="mc-error-text">{formErrors.name_kz}</small>}
              </label>

              <label className="mc-field">
                <span className="mc-label">{L.nameRu}</span>
                <input
                  type="text"
                  value={formData.name_ru}
                  onChange={(e) => setFormData({ ...formData, name_ru: e.target.value })}
                  className={formErrors.name_ru ? "mc-input-error" : ""}
                />
                {formErrors.name_ru && <small className="mc-error-text">{formErrors.name_ru}</small>}
              </label>

              <label className="mc-field mc-field--compact">
                <span className="mc-label">{L.maxPoint}</span>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={formData.max_point}
                  onChange={(e) => setFormData({ ...formData, max_point: e.target.value })}
                  className={formErrors.max_point ? "mc-input-error" : ""}
                />
                {formErrors.max_point && <small className="mc-error-text">{formErrors.max_point}</small>}
              </label>

              <label className="mc-field">
                <span className="mc-label">{L.colCritCat}</span>
                <SearchSelect
                  locale={locale}
                  value={formData.criteria_category_id}
                  onChange={(val) => setFormData({ ...formData, criteria_category_id: val })}
                  options={criteriaCategoryOptions}
                  placeholder={L.pickCritCat}
                  searchPlaceholder={L.searchPh}
                  disabled={criteriaCategoryOptions.length === 0}
                />
                {formErrors.criteria_category_id && (
                  <small className="mc-error-text">{formErrors.criteria_category_id}</small>
                )}
              </label>

              <label className="mc-field">
                <span className="mc-label">{L.colForm}</span>
                <SearchSelect
                  locale={locale}
                  value={formData.criteria_form_id}
                  onChange={(val) => setFormData({ ...formData, criteria_form_id: val })}
                  options={formOptions}
                  placeholder={L.pickForm}
                  searchPlaceholder={L.searchPh}
                  disabled={formOptions.length === 0}
                />
                {formErrors.criteria_form_id && (
                  <small className="mc-error-text">{formErrors.criteria_form_id}</small>
                )}
              </label>

              <label className="mc-field">
                <span className="mc-label">{L.colSubject}</span>
                <SubjectsMultiSelect
                  locale={locale}
                  value={formData.subject_ids}
                  onChange={(ids) => setFormData({ ...formData, subject_ids: ids })}
                  options={subjectOptions}
                  placeholder={L.pickSubject}
                  searchPlaceholder={L.searchPh}
                  disabled={subjectOptions.length === 0}
                />
                <small className="mc-hint">{L.subjectHint}</small>
              </label>

              <label className="mc-field mc-field--full">
                <span className="mc-label">{L.descriptorKz}</span>
                <textarea
                  rows={2}
                  value={formData.descriptor_kz}
                  onChange={(e) => setFormData({ ...formData, descriptor_kz: e.target.value })}
                />
              </label>

              <label className="mc-field mc-field--full">
                <span className="mc-label">{L.descriptorRu}</span>
                <textarea
                  rows={2}
                  value={formData.descriptor_ru}
                  onChange={(e) => setFormData({ ...formData, descriptor_ru: e.target.value })}
                />
                <small className="mc-hint">{L.descriptorHint}</small>
              </label>

              <div className="mc-field mc-field--full">
                <span className="mc-label">{L.indicators}</span>
                <small className="mc-hint">{L.indicatorsHint}</small>

                <div className="mc-indicators">
                  {formData.indicators.map((ind, idx) => (
                    <div className="mc-indicator-row" key={ind.score}>
                      <div className="mc-indicator-score">{L.indicatorScore(ind.score)}</div>
                      <textarea
                        rows={2}
                        className="mc-indicator-text"
                        placeholder={L.indecatorKZ}
                        value={ind.description_kz}
                        onChange={(e) =>
                          setFormData((f) => ({
                            ...f,
                            indicators: f.indicators.map((it, i) =>
                              i === idx ? { ...it, description_kz: e.target.value } : it
                            ),
                          }))
                        }
                      />
                      <textarea
                        rows={2}
                        className="mc-indicator-text"
                        placeholder={L.indecatorRU}
                        value={ind.description_ru}
                        onChange={(e) =>
                          setFormData((f) => ({
                            ...f,
                            indicators: f.indicators.map((it, i) =>
                              i === idx ? { ...it, description_ru: e.target.value } : it
                            ),
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mc-actions">
              {isEditing && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="mc-btn mc-btn-danger"
                  disabled={saving}
                >
                  🗑 {L.del}
                </button>
              )}
              <button type="button" onClick={closeModal} className="mc-btn mc-btn-secondary" disabled={saving}>
                {L.cancel}
              </button>
              <button type="button" onClick={handleSubmit} className="mc-btn mc-btn-primary" disabled={saving}>
                💾 {L.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}